import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { 
  MdPhoto, MdVideocam, MdLocationOn, 
  MdWeb, MdEmail, 
  MdThumbUp, MdComment, MdShare,
  MdEdit, MdVerified, MdMoreHoriz, MdClose,
  MdRepeat, MdSend, MdDelete,
  MdFormatBold, MdFormatItalic, MdFormatUnderlined,
  MdFormatListNumbered, MdFormatListBulleted, MdFormatClear,
  MdPersonAdd, MdPersonRemove, MdSettings, MdCheckCircle, MdRadioButtonUnchecked
} from "react-icons/md";

import AppHeader from "./AppHeader"; 

import { db, storage, auth } from "./firebase";
import { 
  collection, addDoc, getDocs, getDoc, doc, 
  query, orderBy, serverTimestamp, where,
  updateDoc, increment, arrayUnion, arrayRemove, deleteDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";

// --- Styled Components ---

const PageWrapper = styled.div`
  background-color: #f0f2f5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 15px;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @media (min-width: 900px) {
    grid-template-columns: 360px 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #dddfe2;
`;

const CoverImage = styled.div`
  height: 200px;
  background-color: #ccd0d5;
  background-image: url(${props => props.src || ''});
  background-size: cover;
  background-position: center;
`;

const ProfileSection = styled.div`
  padding: 0 16px 16px;
  position: relative;
  text-align: center;
  @media (min-width: 900px) { text-align: left; }
`;

const LogoWrapper = styled.div`
  position: absolute;
  width: 160px;
  height: 160px;
  margin: -80px auto 10px;
  @media (min-width: 900px) { margin: -80px 0 10px 10px; }
`;

const LogoContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: white;
  border: 4px solid white;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const EditIconOverlay = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: #e4e6eb;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  &:hover { background: #d8dadf; }
  svg { color: #1c1e21; font-size: 20px; }
`;

const BusinessInfo = styled.div`
  padding-top: 90px;
  h1 { font-size: 28px; font-weight: 700; color: #1c1e21; margin: 0; display: flex; align-items: center; gap: 5px; justify-content: center; @media (min-width: 900px) { justify-content: flex-start; } }
  p { font-size: 16px; color: #65676b; margin: 4px 0; font-weight: 400; }
`;

const ActionBtn = styled.button`
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.primary ? "#1877f2" : "#e4e6eb"};
  color: ${props => props.primary ? "white" : "#050505"};
  &:hover { opacity: 0.9; }
`;

const RichPostInput = styled.div`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dadce0;
  border-radius: 10px;
  padding: 12px;
  font-size: 16px;
  min-height: 120px;
  outline: none;
  background: white;
  cursor: text;
  line-height: 1.5;
  &:empty:before {
    content: attr(placeholder);
    color: #90949c;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  margin-bottom: 10px;
  button { background: none; border: none; cursor: pointer; padding: 6px; border-radius: 4px; color: #65676b; }
`;

const PostButton = styled.button` 
  background-color: #1a73e8; 
  color: white; 
  border: none; 
  border-radius: 8px; 
  padding: 8px 15px; 
  font-weight: 600; 
  cursor: pointer;
`;

const VideoContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; 
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  margin-top: 10px;
  iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #1c1e21;
  font-size: 15px;
  svg { color: #65676b; font-size: 20px; }
`;

const ActionButton = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 10px;
  cursor: pointer;
  color: ${props => props.active ? '#1877f2' : '#65676b'};
  font-weight: 600;
  border-radius: 4px;
  &:hover { background-color: #f2f2f2; }
`;

const UserListModal = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  width: 90%;
  max-width: 500px;
  border-radius: 12px;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  &:hover { background: #f9f9f9; }
`;

// --- Component Logic ---

const BusinessPageView = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const editEditorRef = useRef(null);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [showManageModal, setShowManageModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);

  // Post States
  const [postText, setPostText] = useState("");
  const [file, setFile] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Edit States
  const [editingPostId, setEditingPostId] = useState(null);

  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*/g;
  
  const getYouTubeId = (text) => {
    const match = new RegExp(youtubeRegex).exec(text);
    return match ? match[1] : null;
  };

  const renderTextWithoutURL = (text) => text ? text.replace(youtubeRegex, "").trim() : "";

  const formatDisplayDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        setCurrentUser(user);
        
        try {
          const profilesSnap = await getDocs(query(collection(db, "profiles"), where("uid", "==", user.uid)));
          
          if (!profilesSnap.empty) {
            const myUsername = profilesSnap.docs[0].id;
            await fetchBusinessData(user, myUsername);
          } else {
            console.error("Profile Document not found for this UID");
            setLoading(false);
            navigate("/");
          }
        } catch (err) {
          console.error("Error fetching username:", err);
          setLoading(false);
          navigate("/");
        }
      } else {
        setLoading(false);
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [businessId]);

  const fetchBusinessData = async (user, usernameToCheck) => {
    try {
      const targetBusinessId = businessId || user.uid;
      const docRef = doc(db, "businessPages", targetBusinessId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const allowedList = data.allowedUsers || [];

        const isOwnerAcc = data.ownerUid === user.uid;
        const hasAccess = allowedList.includes(usernameToCheck);

        if (isOwnerAcc || hasAccess) {
          setBusinessData(data);
          setAllowedUsers(allowedList);
          setIsOwner(isOwnerAcc);
          await fetchBusinessPosts(targetBusinessId);
        } else {
          alert("Access Denied: Neenga intha business page-oda allowed list-il illai.");
          navigate("/");
        }
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Error fetching business data:", err);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "profiles"));
      const users = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u => u.uid !== currentUser.uid);
      setAllUsers(users);
      setShowManageModal(true);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const toggleUserAccess = async (docId) => {
    const targetBusinessId = businessId || currentUser.uid;
    const businessRef = doc(db, "businessPages", targetBusinessId);
    const isAllowed = allowedUsers.includes(docId);
    
    try {
      if (isAllowed) {
        await updateDoc(businessRef, { allowedUsers: arrayRemove(docId) });
        setAllowedUsers(prev => prev.filter(id => id !== docId));
      } else {
        await updateDoc(businessRef, { allowedUsers: arrayUnion(docId) });
        setAllowedUsers(prev => [...prev, docId]);
      }
    } catch (err) {
      console.error("Error updating access:", err);
    }
  };

  const fetchBusinessPosts = async (id) => {
    const q = query(collection(db, "businessPosts"), where("businessId", "==", id), orderBy("createdAt", "desc"));
    const snap = await getDocs(q);
    setPosts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const applyStyle = (command) => {
    document.execCommand(command, false, null);
    if (editorRef.current) editorRef.current.focus();
    if (editEditorRef.current) editEditorRef.current.focus();
  };

  const handleLike = async (post) => {
    const postRef = doc(db, "businessPosts", post.id);
    const isLiked = post.likedBy?.includes(currentUser.uid);
    await updateDoc(postRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(currentUser.uid) : arrayUnion(currentUser.uid)
    });
    fetchBusinessPosts(businessId || currentUser.uid);
  };

  const handleReview = async () => {
    const html = editorRef.current.innerHTML;
    setPostText(html === "<br>" ? "" : html);
    if (file) {
      setIsMediaUploading(true);
      const storageRef = ref(storage, `businessPosts/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on("state_changed", (s) => setUploadProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
        null, async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setPreviewMediaUrl(url);
          setIsMediaUploading(false);
          setIsReviewing(true);
        }
      );
    } else { setIsReviewing(true); }
  };

  const handleUpload = async () => {
    await addDoc(collection(db, "businessPosts"), {
      businessId: businessId || currentUser.uid,
      businessName: businessData.businessName,
      businessLogo: businessData.logoUrl,
      text: postText,
      mediaUrl: previewMediaUrl || null,
      youtubeId: getYouTubeId(postText),
      createdAt: serverTimestamp(),
      likes: 0, comments: 0, likedBy: [],
      fileMetadata: file ? { name: file.name, isVideo: file.type.startsWith('video/') } : null
    });
    setPostText(""); setFile(null); setPreviewMediaUrl(null); setIsReviewing(false);
    if(editorRef.current) editorRef.current.innerHTML = "";
    fetchBusinessPosts(businessId || currentUser.uid);
  };

  const handleUpdatePost = async (postId) => {
    const updatedHtml = editEditorRef.current.innerHTML;
    const postRef = doc(db, "businessPosts", postId);
    
    try {
      await updateDoc(postRef, {
        text: updatedHtml,
        youtubeId: getYouTubeId(updatedHtml)
      });
      setEditingPostId(null);
      fetchBusinessPosts(businessId || currentUser.uid);
    } catch (err) {
      console.error("Error updating post:", err);
    }
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm("Delete this post?")) {
      await deleteDoc(doc(db, "businessPosts", postId));
      fetchBusinessPosts(businessId || currentUser.uid);
    }
  };

  if (loading) return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center'}}>
      <div style={{border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 2s linear infinite'}}></div>
      <p style={{marginTop: '10px', color: '#65676b'}}>Verifying Business Access...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!businessData) return null;

  return (
    <PageWrapper>
      <AppHeader />
      <Container>
        <MainLayout>
          {/* Sidebar */}
          <div>
            <Card>
              <CoverImage src={businessData.coverImageUrl} />
              <ProfileSection>
                <LogoWrapper>
                  <LogoContainer><img src={businessData.logoUrl || "/default-business.png"} alt="logo" /></LogoContainer>
                  {isOwner && (
                    <EditIconOverlay onClick={() => navigate(`/edit-business/${businessId || currentUser.uid}`)}>
                      <MdEdit />
                    </EditIconOverlay>
                  )}
                </LogoWrapper>
                <BusinessInfo>
                  <h1>{businessData.businessName} {businessData.verified && <MdVerified color="#1877f2" />}</h1>
                  <p>{businessData.tagline}</p>
                  
                  <div style={{display:'flex', gap:'10px', flexWrap:'wrap', justifyContent:'center'}}>
                    {isOwner && (
                      <ActionBtn primary onClick={fetchAllUsers}>
                        <MdSettings /> Manage Access
                      </ActionBtn>
                    )}
                   <ActionBtn onClick={() => {
                      const bizSlug = businessData.businessName.toLowerCase().replace(/[\s-]/g, '');
                      const url = `${window.location.origin}/business-view/${bizSlug}`;
                      navigator.clipboard.writeText(url)
                        .then(() => { alert("Link copied to clipboard!"); })
                        .catch(err => { console.error("Failed to copy: ", err); });
                    }}>
                      <MdShare /> Share Page
                    </ActionBtn>
                  </div>
                </BusinessInfo>
              </ProfileSection>
            </Card>
            
            <Card style={{padding: '16px'}}>
              <h3>About</h3>
              <p>{businessData.description}</p>
              <InfoItem><MdLocationOn /> {businessData.location}</InfoItem>
              <InfoItem><MdEmail /> {businessData.email}</InfoItem>
            </Card>
          </div>

          {/* Feed */}
          <div>
            {isOwner && !isReviewing && (
              <Card style={{padding: '15px'}}>
                <Toolbar>
                  <button onClick={() => applyStyle('bold')}><MdFormatBold size={22}/></button>
                  <button onClick={() => applyStyle('italic')}><MdFormatItalic size={22}/></button>
                  <button onClick={() => applyStyle('insertUnorderedList')}><MdFormatListBulleted size={22}/></button>
                </Toolbar>
                <RichPostInput ref={editorRef} contentEditable placeholder="Post an update for your allowed users..." />
                <div style={{display:'flex', justifyContent:'space-between', marginTop:'10px'}}>
                  <div style={{display:'flex', gap:'15px'}}>
                    <label style={{cursor:'pointer'}}><MdPhoto color="#45bd62" size={24}/><input type="file" hidden accept="image/*" onChange={(e)=>setFile(e.target.files[0])}/></label>
                    <label style={{cursor:'pointer'}}><MdVideocam color="#f02849" size={24}/><input type="file" hidden accept="video/*" onChange={(e)=>setFile(e.target.files[0])}/></label>
                  </div>
                  <PostButton onClick={handleReview} disabled={isMediaUploading}>
                    {isMediaUploading ? `Uploading ${uploadProgress}%` : "Review"}
                  </PostButton>
                </div>
              </Card>
            )}

            {isReviewing && (
              <Card style={{padding: '15px'}}>
                <h4>Review Post</h4>
                <div dangerouslySetInnerHTML={{ __html: postText }} style={{ marginBottom: '15px', padding: '10px', background: '#f9f9f9', borderRadius: '8px' }} />
                <div style={{display: 'flex', gap: '10px'}}>
                  <PostButton onClick={handleUpload}>Post Now</PostButton>
                  <PostButton style={{background: '#eee', color: '#333'}} onClick={() => setIsReviewing(false)}>Edit</PostButton>
                </div>
              </Card>
            )}

            {posts.map(post => (
              <Card key={post.id} style={{padding: '15px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                  <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <img src={post.businessLogo || "/default-business.png"} style={{width: '40px', height: '40px', borderRadius: '50%'}} alt="logo" />
                    <div>
                      <h4 style={{margin: 0}}>{post.businessName}</h4>
                      <span style={{fontSize: '12px', color: '#65676b'}}>{formatDisplayDate(post.createdAt)}</span>
                    </div>
                  </div>
                  {isOwner && (
                    <div style={{display: 'flex', gap: '10px'}}>
                      <MdEdit style={{cursor:'pointer', color:'#1877f2'}} onClick={() => setEditingPostId(post.id)} />
                      <MdDelete style={{cursor:'pointer', color:'#f02849'}} onClick={() => handleDeletePost(post.id)} />
                    </div>
                  )}
                </div>

                {editingPostId === post.id ? (
                  <div>
                    <Toolbar>
                      <button onClick={() => applyStyle('bold')}><MdFormatBold size={20}/></button>
                      <button onClick={() => applyStyle('italic')}><MdFormatItalic size={20}/></button>
                    </Toolbar>
                    <RichPostInput 
                      ref={editEditorRef} 
                      contentEditable 
                      dangerouslySetInnerHTML={{ __html: post.text }} 
                      style={{minHeight: '80px', marginBottom: '10px'}}
                    />
                    <div style={{display: 'flex', gap: '10px'}}>
                      <PostButton onClick={() => handleUpdatePost(post.id)}>Save</PostButton>
                      <PostButton style={{background: '#eee', color: '#333'}} onClick={() => setEditingPostId(null)}>Cancel</PostButton>
                    </div>
                  </div>
                ) : (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: renderTextWithoutURL(post.text) }} style={{marginBottom: '10px'}} />
                    {post.youtubeId && <VideoContainer><iframe src={`https://www.youtube.com/embed/${post.youtubeId}`} title="yt" allowFullScreen /></VideoContainer>}
                    {post.mediaUrl && (post.fileMetadata?.isVideo ? <video src={post.mediaUrl} controls style={{width: '100%', borderRadius: '8px'}} /> : <img src={post.mediaUrl} style={{width: '100%', borderRadius: '8px'}} alt="p"/>)}
                  </>
                )}

                <div style={{display: 'flex', borderTop: '1px solid #eee', marginTop: '10px', paddingTop: '5px'}}>
                  <ActionButton active={post.likedBy?.includes(currentUser?.uid)} onClick={() => handleLike(post)}><MdThumbUp /> Like</ActionButton>
                  <ActionButton><MdComment /> Comment</ActionButton>
                </div>
              </Card>
            ))}
          </div>
        </MainLayout>
      </Container>

      {/* --- Manage Access Modal --- */}
      {showManageModal && (
        <UserListModal>
          <ModalContent>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px'}}>
              <h3 style={{margin:0}}>Select Users to Allow Access</h3>
              <MdClose size={24} style={{cursor:'pointer'}} onClick={() => setShowManageModal(false)} />
            </div>
            {allUsers.map(user => (
              <UserItem key={user.id} onClick={() => toggleUserAccess(user.id)}>
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                  <img 
                    src={user.profileImageUrl || "/default-user.png"} 
                    style={{width:'35px', height:'35px', borderRadius:'50%', objectFit: 'cover'}} 
                    alt="u" 
                    onError={(e) => { e.target.src = "/default-user.png" }}
                  />
                  <span>{user.fullName || "User"}</span>
                </div>
                {allowedUsers.includes(user.id) ? <MdCheckCircle color="#1877f2" size={24} /> : <MdRadioButtonUnchecked color="#65676b" size={24} />}
              </UserItem>
            ))}
            <PostButton style={{width:'100%', marginTop:'20px'}} onClick={() => setShowManageModal(false)}>Done</PostButton>
          </ModalContent>
        </UserListModal>
      )}
    </PageWrapper>
  );
};

export default BusinessPageView;